import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  BillingInformationModel,
  StripePaymentSourceModel,
} from '../../../api/billing-types';
import { BillingKeys, deletePaymentMethod } from '../../../api/billing-api';
import { useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeletePaymentMethodDialogProps {
  open: boolean;
  handleClose: () => void;
  billingInfoModel: BillingInformationModel;
  selectedMethod: StripePaymentSourceModel;
  changeLogRoles: string[];
}

export default function DeletePaymentMethodDialog({
  open,
  handleClose,
  selectedMethod,
  billingInfoModel,
  changeLogRoles,
}: DeletePaymentMethodDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deletePaymentMethod(
        billingInfoModel.qbInstance,
        selectedMethod,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Payment method'),
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.CustomerBillingInformation],
      });

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('payment method'),
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.delete_payment_method, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
      });
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canRemovePaymentMethods.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Payment Method"
      dialogContent={
        <Box>
          <Typography>
            Delete this payment method from your account? Deleting it means you
            won't be able to use this payment method for other Promevo purchases
            without adding it again.
          </Typography>
          {selectedMethod.metadata?.isAutoPaySource && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This payment method is currently set as your autopay method.
              Deleting it will disable autopay.
            </Alert>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: isPending,
        title: 'Delete Payment Method',
        color: 'error',
      }}
    />
  );
}
